import { Base64 } from '@js-sdk/encoding';
import { Errors } from '../enums/errors';
import { KeySystem } from '../enums/key_system';
import { FileError } from '../error/file';
import { FragmentError } from '../error/fragment';
import { Content } from './content';
import { createProtectedManifestURL } from './helpers/hls';
/** This value is used to give the network some space, so that not all of the
 * available bandwidth we get from the estimates is consumed for streaming
 * media.
 * We need some breathing room so that other requests can be made during
 * video playback without inducing stuttering.
 */
const OVERHEAD = 1e5;
export class VideoContent extends Content {
    constructor(options) {
        super(options);
        this._assetId = '';
        this._endTime = 0; // TODO: confirm the initial value
        // private _bypassResolve: boolean;
        // TODO: Manifest support
        /**
         * Representation of the video codecs and mimeTypes available for a track.
         */
        this._videoFormats = [];
        this._audioFormats = [];
        /**
         * An array containing the different available subtitle languages for the
         * track.
         */
        this._subtitleLanguages = [];
        /**
         * Current video profile, populated (by a selection made based on the
         * runtime) from a video's manifest file.
         */
        this._videoProfile = null;
        /**
         * All video profiles for this track, populated (by a selection made based on
         * the runtime) from a video's manifest file.
         */
        this._videoProfiles = [];
        /**
         * Current audio profile, populated (by a selection made based on the
         * runtime) from a video's manifest file.
         */
        this._audioProfile = null;
        // TODO: Add docs if it should be here
        this._audioProfiles = [];
        /**
         * FQDN of the currently active CDN, populated from a videos manifest file.
         */
        this._baseURL = '';
        /**
         * String template for constructing the init segment url, populated from a
         * videos manifest file.
         */
        this._initTemplate = '';
        /**
         * String template for constructing urls for fetching segments, populated
         * from a videos manifest file.
         */
        this._segmentTemplate = '';
        // FIXME: Add _subtitleTemplate setter
        this._subtitleTemplate = '';
        this._initTemplate = '';
        this._isAd = options.isAd || false;
        this._mediaType = 'video';
        this._resolver = options.resolver;
        this._videoFormats = options.videoFormats;
        this._audioFormats = options.audioFormats;
        // For some reason, `audio/mp2t; codecs="mp4a.40.2"` is marked as
        // unsupported by both WebKitMediaKeys and HTMLVideoContent, even though it
        // is playable as part of HLS. We therefore add an explicit config here so
        // that we can use it to get the required profile.
        if (this._keySystem === KeySystem.FAIRPLAY) {
            this._audioFormats.unshift({
                mimeType: 'audio/mp2t',
                codec: 'mp4a.40.2',
            });
        }
    }
    static create(options) {
        return new VideoContent(options);
    }
    _updateVariants() {
        const bitrate = this._abrManager.getBandwidthEstimate();
        let videoProfile;
        const profiles = this._videoProfiles;
        const len = profiles.length;
        this._audioProfile = this._audioProfiles[0];
        // TODO: Confirm this ternary below
        const audioBitrate = this._audioProfile
            ? this._audioProfile.audio_bitrate
            : 0;
        for (let i = 0; i < len; i++) {
            const profile = profiles[i];
            if (profile.video_bitrate + audioBitrate < bitrate - OVERHEAD ||
                !videoProfile) {
                videoProfile = profile;
            }
        }
        this._videoProfile = videoProfile || profiles[len - 1];
    }
    /**
     * Constructs a string representation of an audio MimeType.
     *
     * @param profile - Audio profile to base the MimeType on.
     * @return The audio mime type.
     */
    _constructAudioMimeType(profile) {
        if (!profile) {
            return '';
        }
        return `${profile.mime_type};codecs="${profile.audio_codec}"`;
    }
    /**
     * Constructs a string representation of a video MimeType.
     *
     * @param profile - Video profile to base the MimeType on.
     * @return The video mime type.
     */
    _constructVideoMimeType(profile) {
        if (!profile) {
            return '';
        }
        return `${profile.mime_type};codecs="${profile.video_codec}"`;
    }
    _selectPreferredProfile(codec, formats, profiles, encryptionIndex) {
        // The order of preference is based on the EME config that we passed, which
        // is represented here by the `formats` array. We will go through the array
        // to select profiles that match, returning the first set of profiles.
        for (const format of formats) {
            const formatProfiles = [];
            // We will use these two values to "anchor" the rest of the profiles. The
            // first time we encounter a profile that is compatible with a particular
            // format, we will switch the value of these two variables to the
            // mimetype and codec of the profile. Subsequent loops will then compare
            // against these new profiles.
            let preferredMimeType = 'spotify/unknown-mimetype';
            let preferredCodec = 'unknown-codec';
            for (const profile of profiles) {
                if (!(codec in profile) ||
                    profile.encryption_index !== encryptionIndex) {
                    // Quick exit: the profile is not the correct codec or the encryption
                    // is not supported.
                    continue;
                }
                // For Fairplay, we currently only support playback via HLS, and
                // therefore we require 'ts' files. Normally this would be filtered out
                // because of encryption, but in the case of unencrypted files, we need
                // to be explicit about it.
                if (this._keySystem === KeySystem.FAIRPLAY &&
                    profile.file_type !== 'ts') {
                    continue;
                }
                const profileCodec = 'audio_codec' in profile ? profile.audio_codec : profile.video_codec;
                const profileMimeType = profile.mime_type;
                if (profileCodec === preferredCodec &&
                    profileMimeType === preferredMimeType) {
                    // This happens on the second pass, once we've already selected a
                    // format that we want to support.
                    formatProfiles.push(profile);
                }
                else if (profileCodec === format.codec &&
                    profileMimeType === format.mimeType) {
                    // This happens on the first pass. The first format we encounter is
                    // what we want to support.
                    preferredMimeType = profileMimeType;
                    preferredCodec = profileCodec;
                    formatProfiles.push(profile);
                }
            }
            // We now check whether there are profiles. If there were profiles added,
            // we can stop the search as these are the most preferred profiles.
            if (formatProfiles.length) {
                return formatProfiles.sort((a, b) => a.max_bitrate - b.max_bitrate);
            }
        }
        // There were no supported profiles at all, so we'll return an empty array.
        return [];
    }
    /**
     * Constructs URLs for a fragtent.
     *
     * @param fragment - The fragment to get URLs for.
     * @return The segment URLs
     */
    _constructFragmentURL(fragment) {
        // FIXME: This function will always throw error as `this._audioProfile`
        // and `this._videoProfile` only set after this function call.
        // See `_updateVariants`.
        if (!this._audioProfile && !this._videoProfile) {
            throw new Error('Both audioProfile and videoProfile is null');
        }
        if (fragment.init) {
            return this._resolver.getInitSegmentURLs(this._baseURL, this._initTemplate, this._audioProfile, this._videoProfile);
        }
        return this._resolver.getSegmentURLs(this._baseURL, this._segmentTemplate, fragment.timeStart, this._audioProfile, this._videoProfile);
    }
    /**
     * Fetches the buffer bytes for a fragment.
     *
     * @param fragment - The fragment to get bytes for.
     * @return Promise that resolves BufferSet
     */
    _fetchBufferForFragment(fragment) {
        if (!fragment.fetchingBufferPromise) {
            const start = Date.now();
            const resolution = this._videoProfile
                ? this._videoProfile.video_resolution
                : undefined;
            const requestOptions = {
                method: 'GET',
                responseType: 'arraybuffer',
                timing: true,
                retry: {
                    condition: (response, StatusFamily) => {
                        if (response.status === 0) {
                            return true;
                        }
                        const statusFamily = response.getStatusFamily();
                        if (statusFamily === StatusFamily.CONNECTION_ERROR ||
                            statusFamily === StatusFamily.SERVER_ERROR) {
                            return true;
                        }
                        return false;
                    },
                },
            };
            let urls;
            try {
                urls = this._constructFragmentURL(fragment);
            }
            catch (err) {
                return Promise.reject(err);
            }
            fragment.fetchingBufferPromise = Promise.all([
                this._transport
                    .request(urls.audio, requestOptions)
                    .then(this._parseFragmentResponse.bind(this, fragment, 'audio')),
                this._transport
                    .request(urls.video, requestOptions)
                    .then(this._parseFragmentResponse.bind(this, fragment, 'video')),
            ])
                .then(([audioResp, videoResp]) => {
                fragment.quality = resolution;
                fragment.bitrate = this._videoProfile
                    ? this._videoProfile.video_bitrate
                    : undefined;
                if (fragment.cacheBuffer) {
                    fragment.audio.buffer = audioResp.buffer;
                    fragment.audio.bufferURL = urls.audioLogging;
                    // TODO: Confirm this logic
                    if (fragment.video) {
                        fragment.video.buffer = videoResp.buffer;
                        fragment.video.bufferURL = urls.videoLogging;
                    }
                }
                fragment.fetchingBufferPromise = null;
                this._abrManager.sample(audioResp.buffer.byteLength + videoResp.buffer.byteLength, Date.now() - start);
                this._updateVariants();
                return {
                    audio: {
                        bandwidth: audioResp.bandwidth,
                        buffer: audioResp.buffer,
                        bufferURL: urls.audioLogging,
                        byteEnd: fragment.audio.byteEnd,
                        byteStart: fragment.audio.byteStart,
                        codec: fragment.audio.codec,
                    },
                    video: {
                        bandwidth: videoResp.bandwidth,
                        buffer: videoResp.buffer,
                        bufferURL: urls.videoLogging,
                        byteEnd: fragment.video ? fragment.video.byteEnd : 0,
                        byteStart: fragment.video ? fragment.video.byteStart : 0,
                        codec: fragment.video ? fragment.video.codec : '',
                    },
                };
            })
                .catch((e) => {
                fragment.fetchingBufferPromise = null;
                return Promise.reject(e);
            });
        }
        return fragment.fetchingBufferPromise;
    }
    /**
     * Handles responses to fragment requests and parses them.
     *
     * @param fragment - The fragment that should receive the buffer bytes.
     * @param type - MediaType for the buffer, either 'audio' or 'video'.
     * @param response - The HTTP response object from transport.
     * @return Promise that resolves buffer and bandwidth.
     */
    _parseFragmentResponse(fragment, type, response
    // TODO: Fix Promise Type
    ) {
        const timeRange = `${fragment.timeStart}-${fragment.timeEnd}`;
        let codec = null;
        if (fragment[type]) {
            codec = fragment[type];
        }
        switch (response.status) {
            case 0: // Most likely offline, reject so we can retry.
                return Promise.reject(new FragmentError(Errors.FRAGMENT_REQUEST_FAILED_WITH_ZERO, 'Request failed with status 0.', 0, {
                    time_range: timeRange,
                    codec: codec,
                    quality: fragment.quality,
                }));
            case 200:
            case 206: // Successful
                if (!response.body) {
                    return Promise.reject(new FragmentError(Errors.FRAGMENT_REQUEST_EMPTY_RESPONSE, 'Empty response for successful buffer.', response.status, {
                        time_range: timeRange,
                        codec: codec,
                        quality: fragment.quality,
                    }));
                }
                return Promise.resolve({
                    buffer: response.body,
                    bandwidth: (8000 * response.body.byteLength) / response.timing.completed,
                });
            case 403:
                // TODO: handle expired URLs once we know how and if CDN:urls are
                // delivered to us.
                break;
            default:
                break;
        }
        return Promise.reject(new FragmentError(Errors.FRAGMENT_REQUEST_FAILED_WITH_STATUS, `Buffer request failed with status ${response.status}`, response.status, {
            time_range: timeRange,
            codec: codec,
            quality: fragment.quality,
        }));
    }
    _getHLSURLForProfile(profile) {
        const durationSec = this._duration;
        const segmentLength = this._segmentLength;
        const segmentTemplate = this._segmentTemplate;
        const segments = [];
        for (let timestamp = 0; timestamp < durationSec; timestamp += segmentLength) {
            const segmentDuration = timestamp + segmentLength > durationSec
                ? durationSec - timestamp
                : segmentLength;
            const segmentURL = segmentTemplate
                .replace('{{profile_id}}', profile.id)
                .replace('{{segment_timestamp}}', timestamp.toString(10))
                .replace('{{file_type}}', profile.file_type);
            segments.push({
                duration: segmentDuration,
                url: `${this._baseURL}${segmentURL}`,
            });
        }
        return createProtectedManifestURL({
            assetID: this._assetId,
            targetDuration: segmentLength,
            segments: segments,
        });
    }
    /**
     * Calculates the different fragments from the descriptor.
     *
     * @param manifest - The descriptor for the video.
     */
    _calculateFragments(manifest) {
        this._duration = manifest.end_time_millis / 1000;
        const segmentLength = this._segmentLength;
        // TODO: check bitrate, timeStart, timeEnd, byteStart, byteEnd, quality
        // TODO: Check if properties w. -1 should be here
        this._initFragment = {
            init: true,
            cacheBuffer: !this._disableCache,
            timeStart: -1,
            timeEnd: -1,
            quality: 0,
            bitrate: 0,
            audio: {
                bufferURL: null,
                buffer: null,
                codec: this._constructAudioMimeType(this._audioProfile),
                byteStart: -1,
                byteEnd: -1,
            },
            video: {
                bufferURL: null,
                buffer: null,
                codec: this._constructVideoMimeType(this._videoProfile),
                byteStart: -1,
                byteEnd: -1,
            },
        };
        for (let i = 0; i < this._duration; i += segmentLength) {
            this._fragments.push({
                bitrate: 0,
                cacheBuffer: !i && !this._disableCache,
                timeStart: i,
                timeEnd: i + segmentLength,
                quality: 0,
                fetchingBufferPromise: null,
                audio: {
                    bufferURL: null,
                    buffer: null,
                    codec: this._constructAudioMimeType(this._audioProfile),
                    byteStart: -1,
                    byteEnd: -1,
                },
                video: {
                    bufferURL: null,
                    buffer: null,
                    codec: this._constructVideoMimeType(this._videoProfile),
                    byteStart: -1,
                    byteEnd: -1,
                },
            });
        }
    }
    /**
     * Currently a NOOP for video tracks.
     */
    abort() {
        /* NOOP */
    }
    getBitrateForTime(time) {
        if (this._fragments === null) {
            return 0;
        }
        const divisor = this._segmentLength;
        const index = (time - (time % divisor)) / divisor;
        const bitrate = this._fragments[index].bitrate;
        if (bitrate === undefined) {
            return 0;
        }
        return bitrate;
    }
    /**
     * Returns buffer for a fragment, after having fetched the bytes for it.
     *
     * @param fragment - The fragment that should receive the buffer bytes.
     * @return Promise that resolves BufferSet
     */
    getBufferForFragment(fragment) {
        if (fragment &&
            fragment.audio.buffer &&
            fragment.video &&
            fragment.video.buffer &&
            fragment.quality &&
            this._videoProfile &&
            fragment.quality >= this._videoProfile.video_resolution) {
            return Promise.resolve({
                audio: {
                    bufferURL: fragment.audio.bufferURL,
                    buffer: fragment.audio.buffer,
                    codec: fragment.audio.codec,
                    bandwidth: 0,
                    byteStart: -1,
                    byteEnd: -1,
                },
                video: {
                    bufferURL: fragment.video.bufferURL,
                    buffer: fragment.video.buffer,
                    codec: fragment.video.codec,
                    bandwidth: 0,
                    byteStart: -1,
                    byteEnd: -1,
                },
            });
        }
        return this._fetchBufferForFragment(fragment);
    }
    /**
     * Clears all cached buffer for this track.
     */
    clearCachedBuffers() {
        if (this._initFragment) {
            this._initFragment.audio.buffer = null;
            if (this._initFragment.video) {
                this._initFragment.video.buffer = null;
            }
        }
        this._fragments.forEach(function (fragment) {
            fragment.audio.buffer = null;
            if (fragment.video) {
                fragment.video.buffer = null;
            }
        });
    }
    /**
     * @return the track duration.
     */
    getDuration() {
        return this._endTime;
    }
    /**
     * Returns the next fragment after a particular time.
     *
     * @param time - The time in seconds.
     * @return The fragment for the time specified, or null if the time is out of bounds.
     */
    getFragmentAfterTime(time) {
        if (this._fragments === null) {
            return null;
        }
        let fragment = null;
        if (time === 0 || time === 0.01) {
            fragment = this._fragments[1];
        }
        else {
            for (let i = 0, l = this._fragments.length; i < l; i++) {
                if (this._fragments[i].timeStart >= time) {
                    fragment = this._fragments[i];
                    break;
                }
            }
        }
        return fragment;
    }
    /**
     * Returns the fragment for a particular time.
     *
     * @param time - The time in seconds.
     * @return The fragment for the time specified, or null if the time is out of bounds.
     */
    getFragmentForTime(time) {
        if (this._fragments === null) {
            return null;
        }
        let fragment = null;
        if (time === 0 || time === 0.01) {
            fragment = this._fragments[0];
        }
        else {
            for (let i = 0, l = this._fragments.length; i < l; i++) {
                if (this._fragments[i].timeStart <= time &&
                    this._fragments[i].timeEnd >= time) {
                    fragment = this._fragments[i];
                    break;
                }
            }
        }
        return fragment;
    }
    /**
     * Returns the codes for the available subtitle languages for the track.
     *
     * @return The language codes.
     */
    getSubtitleLanguages() {
        return this._subtitleLanguages;
    }
    /**
     * Returns the URL for a specific subtitle file based on its language.
     *
     * @param code - Language code for the specific subtitle file.
     * @return The subtitle files URL.
     */
    // TODO: Confirm this function is needed as '_subtitleTemplate' never set.
    getSubtitleForLanguage(code) {
        return this._subtitleTemplate.replace('{{profile_id}}', code);
    }
    /**
     * Load the VideoContent instance.
     *
     * @param measures - Object containing latency measurements used for
     * logging.
     * @return This instance, for chaining.
     */
    load(measures = {
        resolveLatency: 0,
        manifestLatency: 0,
    }) {
        if (this._loaded) {
            measures.resolveLatency = 0;
            measures.manifestLatency = 0;
            return Promise.resolve(this);
        }
        const timeStart = Date.now();
        return this._resolver
            .getManifest(this._fileId)
            .then((manifest) => {
            measures.resolveLatency = 0;
            measures.manifestLatency = Date.now() - timeStart;
            let error;
            // TODO: Check when it would be possible to have more than 1 content.
            const content = manifest.contents[0];
            if (!content) {
                error = new FileError(Errors.FILE_MALFORMED_SEEKTABLE, 'Malformed seektable: no contents.');
                // Add the content info so end-song logging will work.
                error.track = this.toLogJSON();
                return Promise.reject(error);
            }
            this._resolvedURLs = manifest.base_urls.slice();
            this._baseURL = manifest.base_urls.shift() || '';
            this._resolvedURL = this._baseURL;
            this._fallbackURLs = manifest.base_urls;
            this._initTemplate = manifest.initialization_template;
            this._segmentTemplate = manifest.segment_template;
            this._segmentLength = content.segment_length;
            // Check encryption
            let encryptionIndex = undefined;
            if (content.encryption_infos && content.encryption_infos.length) {
                // The video is private. We need to select the appropriate key
                // system.
                const encryptionInfos = content.encryption_infos;
                const ksName = this._keySystemSettings.commonName;
                let pssh;
                for (let i = 0, l = encryptionInfos.length; i < l; i++) {
                    const encryptionInfo = encryptionInfos[i];
                    if (!encryptionInfo || encryptionInfo.key_system !== ksName) {
                        continue;
                    }
                    encryptionIndex = i;
                    pssh =
                        encryptionInfo[this._keySystemSettings.pssh_field.video];
                    break;
                }
                if (!pssh) {
                    error = new FileError(Errors.FILE_FORMAT_NOT_SUPPORTED, 'KeySystem does not support the file format.');
                    error.track = this.toLogJSON();
                    return Promise.reject(error);
                }
                const keySystemName = this._keySystemSettings.commonName;
                switch (keySystemName) {
                    case 'widevine':
                        this._protection = 'cenc';
                        try {
                            this._psshBox = this._parsePSSHBox(pssh);
                        }
                        catch (e) {
                            error = new FileError(Errors.FILE_MALFORMED_PSSH, 'Invalid PSSH value.');
                            error.track = this.toLogJSON();
                            return Promise.reject(error);
                        }
                        break;
                    case 'fairplay':
                        this._protection = 'hls';
                        this._psshBox = new Uint8Array([]);
                        // Just store the asset id for later
                        try {
                            this._assetId = this._parseAssetID(pssh);
                        }
                        catch (e) {
                            error = new FileError(Errors.FILE_MALFORMED_PSSH, 'Invalid PSSH value.');
                            error.track = this.toLogJSON();
                            return Promise.reject(error);
                        }
                        break;
                    default:
                        error = new FileError(Errors.FILE_FORMAT_NOT_SUPPORTED, 'KeySystem does not support the file format.');
                        error.track = this.toLogJSON();
                        return Promise.reject(error);
                }
            }
            // TODO: Never used
            // this._startTime = manifest.start_time_millis;
            this._endTime = manifest.end_time_millis;
            // Set video and audio profiles
            this._videoProfiles = this._selectPreferredProfile('video_codec', this._videoFormats, content.profiles, encryptionIndex);
            this._audioProfiles = this._selectPreferredProfile('audio_codec', this._audioFormats, content.profiles, encryptionIndex);
            this._updateVariants();
            this._calculateFragments(manifest);
            this._subtitleLanguages = manifest.subtitle_language_codes || [];
            return this;
        });
    }
    /**
     * Returns the duration of the resolve CDN operation..
     *
     * @return The duration resolve CDN operation.
     */
    // TODO: check if we should implament something?
    getResolveLatency() {
        return undefined;
    }
    /**
     * Returns the codec to check playability on the video element with.
     *
     * @return The codec strings
     */
    getPlayableCodecs() {
        // The order is important here. The first codec creates the first source
        // buffer in the Buffer class, which is used to create whether certain times
        // in the content are already buffered. We therefore want to put the codec
        // that is fastest to decode first in the list as it'll give a better
        // indication as to whether a segment is already being processed.
        return [
            this._constructAudioMimeType(this._audioProfile),
            this._constructVideoMimeType(this._videoProfile),
        ];
    }
    /**
     * Returns the AudioProfile for this track.
     *
     * @return The AudioProfile for the track.
     */
    getAudioProfile() {
        return this._audioProfile;
    }
    /**
     * Returns the VideoProfile for this track.
     *
     * @return The VideoProfile for the track.
     */
    getVideoProfile() {
        return this._videoProfile;
    }
    /**
     * Returns all available profiles for this video.
     *
     * @return The array of VideoProfile for the track.
     */
    getVideoProfiles() {
        return this._videoProfiles;
    }
    getHLSURL() {
        if (!this._resolvedURL) {
            throw new FileError(Errors.FILE_NOT_RESOLVED, 'Cannot return HLS Manifest URL: File not resolved.');
        }
        // For HLS, we only support video files of type TS.
        const subManifests = [];
        const audioProfile = this._audioProfiles[0];
        if (audioProfile) {
            subManifests.push(`#EXT-X-MEDIA:TYPE=AUDIO,GROUP-ID="audio",LANGUAGE="eng",NAME="English",AUTOSELECT=YES,URI="${this._getHLSURLForProfile(audioProfile)}"`);
        }
        for (const videoProfile of this._videoProfiles) {
            subManifests.push(`#EXT-X-STREAM-INF:BANDWIDTH=${videoProfile.max_bitrate},RESOLUTION=${videoProfile.video_width}x${videoProfile.video_height},CODECS="${(audioProfile === null || audioProfile === void 0 ? void 0 : audioProfile.audio_codec) ? `mp4a.40.2, ${videoProfile.video_codec}`
                : videoProfile.video_codec}"${(audioProfile === null || audioProfile === void 0 ? void 0 : audioProfile.audio_codec) ? ',AUDIO="audio"' : ''}
${this._getHLSURLForProfile(videoProfile)}`);
        }
        const hlsManifest = `#EXTM3U
${subManifests.join('\n')}`;
        return `data:application/vnd.apple.mpegurl;base64,${Base64.encode(hlsManifest)}`;
    }
}
//# sourceMappingURL=video_content.js.map